<script lang="ts" setup>
    import { betOrderLiveTimeoutDictionary, betOrderOddsTypeDictionary, betOrderPregameTimeoutDictionary } from '@/stores';
    import { IToolUserSettings } from '@/types';
    import { useI18n } from 'vue-i18n';
    import { Currency } from '@/constants';
    import { useUserStore } from '@/stores/user';
    import { useAppStore } from '@/stores/app';
    import debounce from 'lodash-es/debounce';
    import { useTheme } from 'vuetify';
    import { userService } from '@/services/userService';
    import { cloneDeep } from 'lodash-es';
    import { usePlatform } from '@/composables/usePlatform';

    const { toolUserSettingsDialog } = defineProps<{
        toolUserSettingsDialog: IToolUserSettings;
    }>();

    const { t, locale } = useI18n();
    const userStore = useUserStore();
    const appStore = useAppStore();
    const theme = useTheme();
    const platform = usePlatform();

    const orderSettingsForm = useTemplateRef('orderSettingsForm');

    const errorMessage = ref<string>('');

    const suggestedPrices: Record<number, number[]> = {
        [Currency.Euro]: [5, 10, 15, 20, 50, 100],
        [Currency.Gbp]: [5, 10, 15, 20, 50, 100],
        [Currency.Brl]: [6, 10, 15, 20, 50, 100],
        [Currency.Usd]: [5, 10, 15, 20, 50, 100],
    };

    const rules = {
        stake: [
            (v: number | null): string | boolean => {
                if (v !== null) {
                    if (isNaN(Number(v))) {
                        return t('rules.integer');
                    } else if (Number(v) < 0) {
                        return t('rules.negative');
                    } else {
                        let minValue;

                        if (platform.isAggregator()) {
                            minValue = Math.min(...appStore.betOrderMinStakes.filter(([siteId]) => userStore.settings.bet_order_site_ids_allowed.includes(siteId)).map(([, minStake]) => minStake));
                        } else {
                            minValue = Math.min(...appStore.betOrderMinStakes.map(([, minStake]) => minStake));
                        }

                        if (Number(v) < minValue) {
                            return t('rules.minStake', { currency: userStore.currency.symbol, value: minValue });
                        }
                    }
                }
                return true;
            },
        ],
        suggestedPrices(toolUserSettings: IToolUserSettings): string | boolean {
            let v = toolUserSettings.bet_order_suggested_prices_default;
            let result: boolean | string = true;
            if (toolUserSettings.bet_order_is_suggested_prices_default) {
                if (v && v.length === 0) {
                    return t('rules.required');
                }
                if (v && v.length > 6) {
                    return t('rules.max', { value: 6 });
                }
                if (v) {
                    v.forEach(function (item: any) {
                        if (isNaN(Number(item)) || Number(item) <= 0) {
                            result = t('menu.orderSettingsStakesMsg');
                        }
                    });
                }
            }
            return result;
        },
    };

    async function changeOrderSettings(): Promise<void> {
        if (toolUserSettingsDialog.bet_order_is_suggested_prices_default && toolUserSettingsDialog.bet_order_suggested_prices_default === null) {
            toolUserSettingsDialog.bet_order_suggested_prices_default = [...suggestedPrices[userStore.currency.id]];
        }

        if (!toolUserSettingsDialog.bet_order_is_suggested_prices_default) {
            toolUserSettingsDialog.bet_order_suggested_prices_default = null;
        }

        /*if (!toolUserSettingsDialog.bet_order_stake_default) {
            toolUserSettingsDialog.bet_order_stake_default = null;
        }*/

        if (toolUserSettingsDialog.bet_order_suggested_prices_default) {
            toolUserSettingsDialog.bet_order_suggested_prices_default = toolUserSettingsDialog.bet_order_suggested_prices_default.map((v) => Number(v)).filter((v) => !isNaN(v));
        }

        const validateResult = await orderSettingsForm.value!.validate();
        if (validateResult.valid) {
            errorMessage.value = '';
            await userService.changeSettings(toolUserSettingsDialog);
            userStore.settings = cloneDeep(toolUserSettingsDialog);
        } else {
            if (validateResult.errors.length) {
                errorMessage.value = validateResult.errors[0].errorMessages[0];
            }
        }
    }

    const debouncedChangeOrderSettings = debounce(changeOrderSettings, 600);
</script>
<template>
    <v-container class="tab-item-wrapper pa-1">
        <v-form ref="orderSettingsForm" :class="locale === 'en' ? 'en' : 'pt'">
            <v-row class="pt-2" dense>
                <v-text-field
                    variant="outlined"
                    density="compact"
                    type="number"
                    min="0"
                    step="1"
                    :prefix="userStore.currency.symbol"
                    v-model.number="toolUserSettingsDialog.bet_order_stake_default"
                    :label="t('menu.orderSettingsStake')"
                    :rules="rules.stake"
                    color="grey"
                    :hide-details="true"
                    @input="debouncedChangeOrderSettings"
                    @click:clear="debouncedChangeOrderSettings"
                    class="font14 bet"
                    @keydown="
                        (e: KeyboardEvent) => {
                            if (e.key === '.' || e.key === ',' || e.key === '-') e.preventDefault();
                        }
                    "
                    clearable
                ></v-text-field>
            </v-row>
            <v-row v-if="platform.isAggregator()" class="pt-2" dense>
                <v-select
                    v-model="toolUserSettingsDialog.bet_order_odds_default_type_id"
                    :label="t('menu.orderSettingsOdds')"
                    item-value="id"
                    item-title="name"
                    :items="betOrderOddsTypeDictionary.items"
                    variant="outlined"
                    density="compact"
                    required
                    :hide-details="true"
                    @update:modelValue="changeOrderSettings"
                    class="font14 bet"
                    color="grey"
                ></v-select>
            </v-row>
            <v-row v-if="platform.isAggregator()" class="pt-0" dense>
                <v-switch
                    v-model="toolUserSettingsDialog.bet_order_is_max_prices_default"
                    :label="t('menu.orderSettingsOddsButtons')"
                    color="tbGreen"
                    class="mt-0 pt-0 pl-2"
                    :hide-details="true"
                    density="compact"
                    hide-spin-buttons
                    :ripple="false"
                    @change="changeOrderSettings"
                    :true-value="1"
                ></v-switch>
            </v-row>
            <v-row class="pt-2" dense>
                <v-col class="pl-0 py-0 pr-1">
                    <v-select
                        v-model="toolUserSettingsDialog.bet_order_live_timeout_default"
                        :label="t('menu.orderSettingsTimeoutLive')"
                        item-value="id"
                        item-title="name"
                        :items="betOrderLiveTimeoutDictionary.items"
                        density="compact"
                        variant="outlined"
                        hide-details
                        clearable
                        @update:modelValue="changeOrderSettings"
                        class="font14 bet"
                        color="grey"
                    ></v-select>
                </v-col>
                <v-col class="pr-0 py-0 pl-1">
                    <v-select
                        v-model="toolUserSettingsDialog.bet_order_dball_timeout_default"
                        :label="t('menu.orderSettingsTimeoutDball')"
                        item-value="id"
                        item-title="name"
                        :items="betOrderPregameTimeoutDictionary.items"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        @update:modelValue="changeOrderSettings"
                        class="font14 bet"
                        color="grey"
                    ></v-select>
                </v-col>
            </v-row>
            <v-row class="pt-0" dense>
                <v-switch
                    v-model="toolUserSettingsDialog.bet_order_is_suggested_prices_default"
                    :label="t('menu.orderSettingsStakeButtons')"
                    color="tbGreen"
                    class="mt-0 pt-0 pl-2"
                    hide-details
                    :ripple="false"
                    @change="changeOrderSettings"
                    density="compact"
                    hide-spin-buttons
                    :true-value="1"
                ></v-switch>
            </v-row>

            <v-row class="pt-2" dense>
                <v-combobox
                    v-model="toolUserSettingsDialog.bet_order_suggested_prices_default as string[] | null"
                    :label="t('menu.orderSettingsStakes')"
                    :disabled="!toolUserSettingsDialog.bet_order_is_suggested_prices_default"
                    variant="outlined"
                    density="compact"
                    hide-details
                    clearable
                    :rules="[rules.suggestedPrices(toolUserSettingsDialog)]"
                    multiple
                    :items="[]"
                    chips
                    closable-chips
                    class="suggested-prices"
                    @update:modelValue="changeOrderSettings"
                >
                    <template #chip="{ item, props }">
                        <v-chip :color="theme.name.value === 'dark' ? '#40414a' : '#d9ead3'" density="default" variant="flat" v-bind="props">
                            <span class="font13" :class="theme.name.value === 'dark' ? 'whiteText' : 'blackText'">{{ userStore.currency.symbol }}{{ item.value }}</span>
                        </v-chip>
                    </template>
                </v-combobox>
            </v-row>
            <v-row class="v-text-field__details pt-1" dense>
                <div class="v-messages" :class="theme.name.value === 'dark' ? 'whiteText' : 'blackText'" role="alert">
                    <div class="v-messages__wrapper">
                        <div class="v-messages__message">
                            {{ t('menu.orderSettingsStakesHint') }}
                        </div>
                    </div>
                </div>
            </v-row>
            <v-row class="pt-0 mt-0 checkbox-row" dense>
                <v-switch
                    v-model="toolUserSettingsDialog.bet_order_is_top_ups_default"
                    :label="t('menu.orderSettingsIsTopUps')"
                    class="mt-0 pt-0 pl-2"
                    density="compact"
                    hide-details
                    :ripple="false"
                    color="tbGreen"
                    :false-value="0"
                    :true-value="1"
                    @change="changeOrderSettings"
                ></v-switch>
            </v-row>
            <v-row class="pt-0 mt-0 checkbox-row" dense>
                <v-switch
                    v-model="toolUserSettingsDialog.bet_order_is_request_bet_confirmation_default"
                    :label="t('menu.orderSettingsIsConfirmBet')"
                    class="mt-0 pt-0 pl-2"
                    density="compact"
                    hide-details
                    :ripple="false"
                    color="tbGreen"
                    :false-value="0"
                    :true-value="1"
                    @change="changeOrderSettings"
                ></v-switch>
            </v-row>
            <v-row class="pt-0 mt-0 checkbox-row" dense>
                <v-switch
                    v-model="toolUserSettingsDialog.bet_order_is_request_bet_duplicate_confirmation_default"
                    :label="t('menu.orderSettingsIsConfirmDuplicateBet')"
                    class="mt-0 pt-0 pl-2"
                    density="compact"
                    hide-details
                    :ripple="false"
                    color="tbGreen"
                    :false-value="0"
                    :true-value="1"
                    @change="changeOrderSettings"
                ></v-switch>
            </v-row>
            <v-row class="pt-0 mt-0 checkbox-row" dense>
                <v-switch
                    v-model="toolUserSettingsDialog.bet_order_is_keep_order_popup_open_default"
                    :label="t('menu.orderSettingsIsKeepOrderPopupOpen')"
                    class="mt-0 pt-0 pl-2"
                    density="compact"
                    hide-details
                    hide-spin-buttons
                    color="tbGreen"
                    :ripple="false"
                    :false-value="0"
                    :true-value="1"
                    @change="changeOrderSettings"
                ></v-switch>
            </v-row>
            <v-row class="pt-1 ma-0" style="height: 26px" dense>
                <span class="font11 pl-1 d-flex flex-column align-center justify-center validation-error">
                    {{ errorMessage }}
                </span>
            </v-row>
        </v-form>
    </v-container>
</template>
<style scoped>
    .suggested-prices :deep(.v-combobox__selection) {
        margin-bottom: 4px;
    }

    .v-theme--light.suggested-prices :deep(.v-field .v-icon) {
        color: black;
    }

    .suggested-prices :deep(.v-field__input) {
        padding-top: 10px;
    }

    .checkbox-row {
        height: 34px;
    }

    :deep(.v-input .v-field) {
        --v-field-padding-start: 12px;
        --v-field-padding-end: 2px !important;
    }

    :deep(.pt .v-input .v-field .v-label) {
        font-size: 11px !important;
    }
</style>
